import { motion } from "framer-motion"
import { Link, useParams } from "react-router-dom"
import { Button } from "~/components/Button"
import { GlobalNav } from "~/components/GlobalNav"
import { TokenPanel } from "~/components/TokenPanel"
import { VotePanel } from "~/components/VotePanel"
import { formatLargeBalance } from "~/utils/formatLargeBalance"
import { formatProposalDisplayId, extractGithubIssueNumber } from "~/utils/github"
import { useGameDataByRepository, type GameData } from "~/hooks/useGameDataByRepository"
import { useGameProposals, type Proposal } from "~/hooks/useGameProposals"

type TaskItem = {
  id: string;
  type: string;
  title: string;
  description: string;
  link?: string;
  commits?: string;
  comments?: string;
  votes: [number, number];
}

type Task = {
  id: string;
  title: string;
  description: string;
  githubIssueUrl?: string;
  difficulty: {
    value: string;
    label: string;
  };
  votes: {
    approve: [number, number];
    delete: [number, number];
  };
  items: TaskItem[];
}

export default function GameDetail() {
  const { repositoryName } = useParams<{ repositoryName: string }>()
  const { game, isLoading: gameLoading, error: gameError } = useGameDataByRepository({ repositoryName: repositoryName! })
  const { proposals, isLoading: proposalsLoading, error: proposalsError } = useGameProposals({ gameId: game?.id || '' })

  const isLoading = gameLoading || proposalsLoading
  const error = gameError || proposalsError

  if (isLoading) {
    return (
      <div>
        <GlobalNav />
        <div className="flex justify-center items-center h-96">
          <p className="text-white">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <GlobalNav />
        <div className="flex justify-center items-center h-96">
          <p className="text-red-500">Error: {error.message}</p>
        </div>
      </div>
    )
  }

  if (!game) {
    return (
      <div>
        <GlobalNav />
        <div className="flex justify-center items-center h-96">
          <p className="text-white">Game not found</p>
        </div>
      </div>
    )
  }

  return (
    <div className="">
      <GlobalNav />

      <div className="flex gap-6 pt-6 px-10">
        <div className="flex-1 flex flex-col gap-6">
          <Play game={game} />
          <Info game={game} />
          <TaskBoard game={game} proposals={proposals} repositoryName={repositoryName!} />
        </div>

        <div className="sticky top-0 flex flex-col gap-6 w-[320px]">
          <TokenPanel game={game} />
          <VotePanel game={game} />
        </div>
      </div>
    </div>
  )
}

const Play = ({ game }: { game: GameData }) => {
  return (
    <div className="flex justify-center items-center h-[580px] w-full rounded-lg bg-gray-900">
      <Button>
        Play Game
      </Button>
    </div>
  )
}

const Info = ({ game }: { game: GameData }) => {
  return (
    <div className="p-6 rounded-lg border border-solid border-[#212425]">
      <div className="flex flex-col gap-4">
        <div className="flex gap-2.5">
          <p className="flex-1 text-[28px] font-semibold text-white">
            {game.game.name}
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 pl-2">
              <div className="flex items-center relative gap-1">
                <svg
                  width={16}
                  height={17}
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 relative"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M4.44453 6.06138C5.42637 6.06138 6.2223 5.26415 6.2223 4.28072C6.2223 3.29729 5.42637 2.50006 4.44453 2.50006C3.46269 2.50006 2.66675 2.79729 2.66675 4.28072C2.66675 5.26415 3.46269 6.06138 4.44453 6.06138ZM4.44453 6.06138V13.8778M11.5556 10.3165C12.5375 10.3165 13.3334 11.1137 13.3334 12.0972C13.3334 13.0806 12.5375 13.8778 11.5556 13.8778C10.5738 13.3778 9.77786 12.5806 9.77786 12.0972C9.77786 11.1137 10.5738 10.3165 11.5556 10.3165ZM11.5556 10.3165V6.08511C11.5556 5.10327 10.7597 4.30733 9.77786 4.30733H8.71119"
                    stroke="#6B6D6E"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="text-[13px] font-medium text-[#6b6d6e]">
                  {game.github.pr}
                </p>
              </div>
              <div className="flex items-center relative gap-1">
                <svg
                  width={16}
                  height={17}
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 relative"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M10.191 8.49999H14.4001M1.60015 8.38196H6.20814M6.20438 8.49999C6.20438 9.50651 7.02032 10.3225 8.02685 10.3225C9.03337 10.3225 9.84931 9.50651 9.84931 8.49999C9.84931 7.49347 9.03337 6.67752 8.02685 6.67752C7.02032 6.67752 6.20438 7.49347 6.20438 8.49999Z"
                    stroke="#6B6D6E"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                  />
                </svg>
                <p className="text-[13px] font-medium text-[#6b6d6e]">
                  {game.github.commits}
                </p>
              </div>
              <div className="flex items-center relative gap-1">
                <svg
                  width={16}
                  height={17}
                  viewBox="0 0 16 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 relative"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M7.99984 3.10135L9.64784 6.44002L13.3332 6.97868L10.6665 9.57602L11.2958 13.2453L7.99984 11.512L4.70384 13.2453L5.33317 9.57602L2.6665 6.97868L6.35184 6.44002L7.99984 3.10135Z"
                    stroke="#6B6D6E"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="text-[13px] font-medium text-[#6b6d6e]">
                  {game.github.stars}
                </p>
              </div>
            </div>
            <a target="_blank" rel="noreferrer" href={game.github.link} className="flex items-center relative gap-1 p-2 rounded bg-[#212425]">
              <svg
                width={16}
                height={17}
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 relative"
                preserveAspectRatio="none"
              >
                <path
                  d="M6.8679 9.06611C7.11101 9.39112 7.42118 9.66004 7.77736 9.85464C8.13354 10.0492 8.52741 10.165 8.93225 10.194C9.33709 10.223 9.74343 10.1645 10.1237 10.0227C10.504 9.88083 10.8493 9.65885 11.1363 9.3718L12.8345 7.67351C13.3501 7.13968 13.6354 6.4247 13.629 5.68256C13.6225 4.94042 13.3249 4.2305 12.8001 3.7057C12.2753 3.18091 11.5653 2.88323 10.8232 2.87679C10.0811 2.87034 9.36608 3.15563 8.83225 3.67123L7.85856 4.63925M9.13228 7.93392C8.88917 7.60891 8.579 7.33998 8.22282 7.14538C7.86664 6.95078 7.47277 6.83506 7.06793 6.80607C6.66309 6.77707 6.25675 6.83549 5.87647 6.97734C5.49619 7.1192 5.15087 7.34118 4.86392 7.62823L3.16564 9.32651C2.65005 9.86034 2.36475 10.5753 2.3712 11.3175C2.37765 12.0596 2.67533 12.7695 3.20012 13.2943C3.72491 13.8191 4.43483 14.1168 5.17697 14.1232C5.91911 14.1297 6.6341 13.8444 7.16793 13.3288L8.13595 12.3608"
                  stroke="white"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-[13px] font-semibold text-white">
                Github
              </p>
            </a>
          </div>
        </div>
        <p className="text-base font-medium text-[#6b6d6e]">
          {game.game.description}
        </p>
      </div>
    </div>
  )
}

const TaskBoard = ({ game, proposals, repositoryName }: { game: GameData, proposals: Proposal[], repositoryName: string }) => {
  // Group proposals by their status and difficulty
  const { high, medium, low, pending } = proposals.reduce((acc, proposal) => {
    // Determine difficulty based on bountyBps
    let difficulty = 'low'
    if (proposal.bountyBps >= 1000) difficulty = 'high'
    else if (proposal.bountyBps >= 500) difficulty = 'medium'
    
    const task = {
      id: proposal.proposalId,
      title: proposal.title,
      description: proposal.description,
      githubIssueUrl: proposal.githubIssueUrl,
      difficulty: {
        value: difficulty,
        label: difficulty.charAt(0).toUpperCase() + difficulty.slice(1),
      },
      votes: {
        approve: [proposal.approveVotesFor || 0, proposal.approveVotesAgainst || 0] as [number, number],
        delete: [proposal.deleteVotesFor || 0, proposal.deleteVotesAgainst || 0] as [number, number],
      },
      items: proposal.works.map(work => ({
        id: work.workId,
        type: 'pr',
        title: work.title,
        description: work.title,
        votes: [0, 0] as [number, number], // Work votes not available in API yet
      })),
    }

    // If the proposal is still being voted on, add to pending
    if (proposal.status === 'voting') {
      acc.pending.push(task)
    } else {
      // Otherwise add to the appropriate difficulty category
      acc[difficulty].push(task)
    }
    
    return acc
  }, { high: [], medium: [], low: [], pending: [] } as Record<string, Task[]>)

  return (
    <div className="flex flex-col gap-8 p-6 rounded-lg border border-solid border-[#212425]">
      <div className="flex items-center relative gap-2">
        <p className="flex-1 text-2xl font-semibold text-left text-white">Taskboard</p>
        <Link to={`/games/${repositoryName}/tasks/create`} className="flex justify-center items-center relative gap-1 px-3 py-2 rounded bg-[#212425]">
          <svg
            width={16}
            height={16}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4 relative"
            preserveAspectRatio="none"
          >
            <path d="M8 4L8 12M12 8L4 8" stroke="white" strokeWidth="1.33333" strokeLinecap="round" />
          </svg>
          <p className="text-[13px] font-semibold text-left text-white">
            Propose Task
          </p>
        </Link>
      </div>

      <div className="flex flex-col gap-4">
        {/* Pending Task Proposals */}
        {pending.length > 0 && (
          <>
            <div className="flex items-center relative gap-2">
              <p className="text-xl text-left text-white">Pending Task Proposals</p>
              <p className="text-lg font-semibold text-left text-[#ffd700]">{pending.length}</p>
            </div>
            {pending.map((task) => (
              <TaskPanel 
                key={task.id} 
                game={game} 
                task={task} 
                reward={task.difficulty.value === 'high' ? 1000 : task.difficulty.value === 'medium' ? 500 : 100}
                isPending={true}
                repositoryName={repositoryName}
              />
            ))}
          </>
        )}

        {/* Active Tasks */}
        <div className="flex items-center relative gap-2">
          <p className="text-xl text-left text-white">High Impact</p>
          <p className="text-lg font-semibold text-left text-[#60e25e]">{high.length}</p>
        </div>
        {high.map((task) => (
          <TaskPanel 
            key={task.id} 
            game={game} 
            task={task} 
            reward={1000} // High impact reward
            repositoryName={repositoryName}
          />
        ))}

        <div className="flex items-center relative gap-2">
          <p className="text-xl text-left text-white">Medium Impact</p>
          <p className="text-lg font-semibold text-left text-[#60e25e]">{medium.length}</p>
        </div>
        {medium.map((task) => (
          <TaskPanel 
            key={task.id} 
            game={game} 
            task={task} 
            reward={500} // Medium impact reward
            repositoryName={repositoryName}
          />
        ))}

        <div className="flex items-center relative gap-2">
          <p className="text-xl text-left text-white">Low Impact</p>
          <p className="text-lg font-semibold text-left text-[#60e25e]">{low.length}</p>
        </div>
        {low.map((task) => (
          <TaskPanel 
            key={task.id} 
            game={game} 
            task={task} 
            reward={100} // Low impact reward
            repositoryName={repositoryName}
          />
        ))}
      </div>
    </div>
  )
}

const TaskPanel = ({ game, task, reward, isPending = false, repositoryName }: { 
  game: GameData, 
  task: Task, 
  reward: number, 
  isPending?: boolean,
  repositoryName: string
}) => {
  // Format the display ID using GitHub issue ID if available
  const displayId = formatProposalDisplayId(task.id, task.githubIssueUrl)
  
  // Extract issue number for URL
  const issueNumber = extractGithubIssueNumber(task.githubIssueUrl)
  const taskUrl = issueNumber 
    ? `/games/${repositoryName}/tasks/${issueNumber}`
    : `/games/${repositoryName}/tasks/${task.id}` // Fallback to ID if no issue number
  
  return (
    <div className={`flex flex-col gap-4 px-4 py-3 rounded-lg border border-solid ${isPending ? 'border-[#ffd700]/30 bg-[#ffd700]/5' : 'border-[#212425]'}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center relative gap-4">
          <p className="text-base font-medium text-left text-white">
            {task.title} {displayId}
          </p>
          {isPending && (
            <div className="flex items-center gap-4">
              <span className="px-2 py-1 text-xs font-medium text-[#ffd700] bg-[#ffd700]/10 rounded">
                Voting
              </span>
              <div className="flex items-center gap-4">
                <div className="flex flex-col items-end">
                  <span className="text-xs text-[#9c9e9e]">Approve</span>
                  <VoteProgress votes={task.votes.approve} />
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-xs text-[#9c9e9e]">Delete</span>
                  <VoteProgress votes={task.votes.delete} />
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-center items-center gap-2">
            <div className="flex items-center relative gap-1">
              <svg
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 relative"
                preserveAspectRatio="none"
              >
                <path
                  d="M4.44453 5.56138C5.42637 5.56138 6.2223 4.76415 6.2223 3.78072C6.2223 2.79729 5.42637 2.00006 4.44453 2.00006C3.46269 2.00006 2.66675 2.79729 2.66675 3.78072C2.66675 4.76415 3.46269 5.56138 4.44453 5.56138ZM4.44453 5.56138V13.3778M11.5556 9.81652C12.5375 9.81652 13.3334 10.6137 13.3334 11.5972C13.3334 12.5806 12.5375 13.3778 11.5556 13.3778C10.5738 13.3778 9.77786 12.5806 9.77786 11.5972C9.77786 10.6137 10.5738 9.81652 11.5556 9.81652ZM11.5556 9.81652V5.58511C11.5556 4.60327 10.7597 3.80733 9.77786 3.80733H8.71119"
                  stroke="#6B6D6E"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-[13px] font-medium text-left text-[#6b6d6e]">
                {task.items.filter((item) => item.type === 'pr').length}
              </p>
            </div>
            <div className="flex items-center relative gap-2.5 py-[3px]">
              <div className="w-px bg-[#2d3031]" />
            </div>
            <div className="flex items-center relative gap-1">
              <svg
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 relative"
                preserveAspectRatio="none"
              >
                <path
                  d="M7.99992 5.46665V3.33331M7.99992 12.6666V10.5333M10.5333 7.99998H12.6666M3.33325 7.99998H5.46659M9.79142 6.20873L11.2999 4.70024M4.69976 11.2999L6.20825 9.79139M9.79142 9.79123L11.2999 11.2997M4.69976 4.70008L6.20825 6.20857"
                  stroke="#6B6D6E"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-[13px] font-medium text-left text-[#6b6d6e]">
                {task.items.filter((item) => item.type === 'vote').length}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex justify-end items-center relative gap-4">
            <p className="text-[13px] font-medium text-left text-[#09d26a]">
              (Earn {formatLargeBalance(game.token.devPool*reward)} {game.token.symbol})
            </p>
          </div>
          <Link to={taskUrl} className="flex justify-center items-center relative gap-1 px-3 py-2 rounded bg-[#212425]">
            <p className="text-[13px] font-semibold text-left text-white">
              Details
            </p>
            <svg
              width={16}
              height={16}
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 relative"
              preserveAspectRatio="none"
            >
              <path
                d="M6.66667 4.66669L10 8.00002L6.66667 11.3334"
                stroke="white"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {task.items.map((item) => (
          <TaskItem key={item.id} item={item}>
            {null}
          </TaskItem>
        ))}
      </div>
    </div>
  )
}

const TaskItem = ({ item, children }: { item: TaskItem, children: React.ReactNode }) => {
  const Comp = item.type === 'pr' ? motion.a : motion.div;

  return (
    <Comp 
      key={item.id} 
      className="flex items-center gap-4 px-4 py-3 rounded-lg bg-white/5 border border-[#212425]" 
      {...(item.type === 'pr' ? { href: item.link, target: '_blank', rel: 'noreferrer' } : {})}
    >
      <div className="flex-1 flex relative gap-2">
        {item.type === 'pr' ? (
          <svg
            width={16}
            height={16}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4 relative"
            preserveAspectRatio="none"
          >
            <path
              d="M4.44453 5.56138C5.42637 5.56138 6.2223 4.76415 6.2223 3.78072C6.2223 2.79729 5.42637 2.00006 4.44453 2.00006C3.46269 2.00006 2.66675 2.79729 2.66675 3.78072C2.66675 4.76415 3.46269 5.56138 4.44453 5.56138ZM4.44453 5.56138V13.3778M11.5556 9.81652C12.5375 9.81652 13.3334 10.6137 13.3334 11.5972C13.3334 12.5806 12.5375 13.3778 11.5556 13.3778C10.5738 13.3778 9.77786 12.5806 9.77786 11.5972C9.77786 10.6137 10.5738 9.81652 11.5556 9.81652ZM11.5556 9.81652V5.58511C11.5556 4.60327 10.7597 3.80733 9.77786 3.80733H8.71119"
              stroke="white"
              strokeWidth="1.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : item.type === 'vote' ? (
          <svg
            width={16}
            height={16}
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="flex-grow-0 flex-shrink-0 w-4 h-4 relative"
            preserveAspectRatio="none"
          >
            <path
              d="M13.1745 8.16675H2.82549C2.09344 8.16675 1.5 8.76019 1.5 9.49224V11.8413C1.5 12.5733 2.09344 13.1667 2.82549 13.1667H13.1745C13.9066 13.1667 14.5 12.5733 14.5 11.8413V9.49224C14.5 8.76019 13.9066 8.16675 13.1745 8.16675Z"
              fill="#080C0D"
              stroke="white"
              strokeLinecap="round"
            />
            <path
              d="M10.5077 4H5.49199C5.03609 4 4.6665 4.36958 4.6665 4.82549V10.6667H11.3332V4.82549C11.3332 4.36958 10.9636 4 10.5077 4Z"
              fill="#080C0D"
              stroke="white"
              strokeLinecap="round"
            />
            <path d="M4 10.6667H12" stroke="white" strokeLinecap="round" />
          </svg>
        ) : (
          <></>
        )}
        <div className="flex flex-col justify-center items-start relative gap-1">
          <div className="flex items-center relative gap-2.5">
            <p className="text-sm font-medium text-white">
              {item.title}
            </p>
            <div className="flex justify-center items-center gap-2">
              <div className="flex items-center relative gap-0.5">
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 relative"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M10.191 8.00002H14.4001M1.60015 7.88199H6.20814M6.20438 8.00002C6.20438 9.00654 7.02032 9.82249 8.02685 9.82249C9.03337 9.82249 9.84931 9.00654 9.84931 8.00002C9.84931 6.9935 9.03337 6.17755 8.02685 6.17755C7.02032 6.17755 6.20438 6.9935 6.20438 8.00002Z"
                    stroke="#6B6D6E"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                  />
                </svg>
                <p className="text-[13px] font-medium text-[#6b6d6e]">
                  {item.commits}
                </p>
              </div>
              <div className="flex items-center relative gap-0.5">
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 relative"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M5.78261 12L7.86957 9.91304H11.1667C11.6269 9.91304 12 9.53995 12 9.07971V4.83333C12 4.3731 11.6269 4 11.1667 4H4.83333C4.3731 4 4 4.3731 4 4.83333V9.07971C4 9.53995 4.3731 9.91304 4.83333 9.91304H5.78261V12Z"
                    stroke="#6B6D6E"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="text-[13px] font-medium text-[#6b6d6e]">
                  {item.comments}
                </p>
              </div>
            </div>
          </div>
          <p className="text-[13px] text-[#6b6d6e]">
            {item.description}
          </p>
        </div>
      </div>
      
      <VoteProgress votes={item.votes} />

      {children}
    </Comp>
  )
}

const VoteProgress = ({ votes }: { votes: [number, number] }) => {
  // Ensure we have at least two elements in the votes array
  const yes = votes[0] || 0;
  const no = votes[1] || 0;
  const total = yes + no;
  
  // Handle division by zero
  const yesPercentage = total === 0 ? 0 : (yes / total * 100);
  const noPercentage = total === 0 ? 0 : (no / total * 100);
  
  // Determine colors based on vote state
  const yesColor = yes > 0 ? '#60e25e' : '#6b6d6e';
  const noColor = no > 0 ? '#ff4949' : '#6b6d6e';
  
  return (
    <div className="flex flex-col relative gap-1">
      <div className="flex justify-between">
        <div className="flex items-center relative gap-1">
          <p className={`text-[13px] font-bold text-left`} style={{ color: yesColor }}>
            {yes}
          </p>
          <p className={`text-[11px] font-medium text-left`} style={{ color: yesColor }}>
            ({total === 0 ? '0' : yesPercentage.toFixed(0)}%)
          </p>
        </div>
        <div className="flex justify-end items-center relative gap-1">
          <p className={`text-[11px] font-medium text-left`} style={{ color: noColor }}>
            ({total === 0 ? '0' : noPercentage.toFixed(0)}%)
          </p>
          <p className={`text-[13px] font-bold text-left`} style={{ color: noColor }}>
            {no}
          </p>
        </div>
      </div>
      <div className="relative flex items-center rounded-full overflow-hidden bg-[#212425] w-40 h-1">
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${total === 0 ? '0' : yesPercentage}%`,
          backgroundColor: yesColor,
        }} />
        <div style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: `${total === 0 ? '0' : noPercentage}%`,
          backgroundColor: noColor,
        }} />
      </div>
    </div>
  );
}; 